import React, { useState } from 'react';
import axios from 'axios';
import { Clock, Send } from 'lucide-react';
import Dropdown from '../../Components/utils/Dropdown';
import countries from '../../data/countries.json';
import { showToast } from '../../Components/utils/ShowToast';
import Loader from '../../Components/utils/Loader';
import SEO from '../../Components/utils/SEO';

const ContactUs = () => {
    const [formData, setFormData] = useState({
        fullName: '',
        email: '',
        country: null,
        phoneNumber: '',
        subject: '',
        message: '',
        inquiryType: null,
    });
    const [loading, setLoading] = useState(false);

    // Map countries.json to Dropdown options format
    const countryOptions = countries.map(country => ({
        label: country.name,
        value: country.code,
    }));

    // Define inquiry type options
    const inquiryOptions = [
        { label: 'General Inquiry', value: 'general' },
        { label: 'Support', value: 'support' },
        { label: 'Sales', value: 'sales' },
        { label: 'Feedback', value: 'feedback' },
    ];

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setLoading(true);
            const dataToSend = {
                fullName: formData.fullName,
                email: formData.email,
                country: formData.country ? formData.country.label : '',
                phoneNumber: formData.phoneNumber,
                subject: formData.subject,
                message: formData.message,
                inquiryType: formData.inquiryType ? formData.inquiryType.label : '',
            };
            const response = await axios.post(
                `${process.env.REACT_APP_Backend_Host_Name}/api/v1/settings/admin/receive/email`,
                dataToSend
            );
            if (response.data.success) {
                showToast('Message sent successfully!', 'success');
                // Reset form
                setFormData({
                    fullName: '',
                    email: '',
                    country: null,
                    phoneNumber: '',
                    subject: '',
                    message: '',
                    inquiryType: null,
                });
            }
        } catch (error) {
            console.error(error);
            showToast(error.response?.data?.error || 'Failed to send message', 'error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="contactUsPage">
            <SEO
                title="Contact"
                description="Get in touch with Chocowi support, sales, or feedback. We typically respond within 24 hours."
                canonicalPath="/contact"
            />
            <section className="contactUsHero">
                <h1 className="contactUsTitle">Get in Touch</h1>
                <p className="contactUsSubtitle">
                    Have a question or feedback? We'd love to hear from you. Our team typically responds within 24 hours.
                </p>
            </section>

            <div className="contactUsWrapper">
                <div className="contactUsContent">
                    <div className="contactUsFormCard">
                        <h2 className="contactUsFormTitle">Send us a message</h2>
                        <form onSubmit={handleSubmit} className="contactUsForm">
                            <div className="contactUsFormGroup">
                                <label htmlFor="inquiryType">Inquiry Type</label>
                                <Dropdown
                                    options={inquiryOptions}
                                    value={formData.inquiryType}
                                    onChange={(selectedOption) => setFormData({ ...formData, inquiryType: selectedOption })}
                                    placeholder="Select inquiry type"
                                    required
                                    disabled={loading}
                                />
                            </div>
                            <div className="contactUsFormRow">
                                <div className="contactUsFormGroup">
                                    <label htmlFor="fullName">Full Name</label>
                                    <input
                                        id="fullName"
                                        type="text"
                                        value={formData.fullName}
                                        onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                                        required
                                        className="contactUsInput"
                                        placeholder="Your full name"
                                        disabled={loading}
                                    />
                                </div>
                                <div className="contactUsFormGroup">
                                    <label htmlFor="email">Email</label>
                                    <input
                                        id="email"
                                        type="email"
                                        value={formData.email}
                                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                        required
                                        className="contactUsInput"
                                        placeholder="your@email.com"
                                        disabled={loading}
                                    />
                                </div>
                            </div>
                            <div className="contactUsFormGroup">
                                <label htmlFor="country">Country</label>
                                <Dropdown
                                    options={countryOptions}
                                    value={formData.country}
                                    onChange={(selectedOption) => setFormData({ ...formData, country: selectedOption })}
                                    placeholder="Select your country"
                                    required
                                    disabled={loading}
                                />
                            </div>
                            <div className="contactUsFormGroup">
                                <label htmlFor="phoneNumber">Phone Number</label>
                                <input
                                    id="phoneNumber"
                                    type="tel"
                                    value={formData.phoneNumber}
                                    onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                                    required
                                    className="contactUsInput"
                                    placeholder="+1 (555) 000-0000"
                                    disabled={loading}
                                />
                            </div>
                            <div className="contactUsFormGroup">
                                <label htmlFor="subject">Subject</label>
                                <input
                                    id="subject"
                                    type="text"
                                    value={formData.subject}
                                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                                    required
                                    className="contactUsInput"
                                    placeholder="Brief subject of your inquiry"
                                    disabled={loading}
                                />
                            </div>
                            <div className="contactUsFormGroup">
                                <label htmlFor="message">Message</label>
                                <textarea
                                    id="message"
                                    value={formData.message}
                                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                                    required
                                    className="contactUsInput contactUsTextarea"
                                    rows="5"
                                    placeholder="Tell us how we can help..."
                                    disabled={loading}
                                />
                            </div>
                            <button type="submit" className="contactUsSubmitBtn" disabled={loading}>
                                {loading ? <Loader type="miniPlus" /> : (
                                    <>
                                        <Send size={18} strokeWidth={2} />
                                        Send Message
                                    </>
                                )}
                            </button>
                        </form>
                    </div>

                    <div className="contactUsSidebar">
                        <div className="contactUsInfoCard">
                            <h3 className="contactUsInfoTitle">How to Reach Us</h3>
                            <div className="contactUsInfoList">
                                <div className="contactUsInfoItem">
                                    <div className="contactUsInfoIcon">
                                        <Send size={20} strokeWidth={1.5} />
                                    </div>
                                    <div>
                                        <span className="contactUsInfoLabel">Contact Form</span>
                                        <span className="contactUsInfoValue">All enquiries are handled through this form</span>
                                    </div>
                                </div>
                                <div className="contactUsInfoItem">
                                    <div className="contactUsInfoIcon">
                                        <Clock size={20} strokeWidth={1.5} />
                                    </div>
                                    <div>
                                        <span className="contactUsInfoLabel">Response Time</span>
                                        <span className="contactUsInfoValue">Within 24 hours</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="contactUsHelpCard">
                            <p className="contactUsHelpText">
                                <strong>Need urgent assistance?</strong> For order-related issues, please include your order ID in your message for faster support.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ContactUs;